import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useNavigationStore } from '@stores/navigationStore';
import { consumeBrowserAnimated } from '@utils/browserGesture';

interface PageTransitionProps {
  children: ReactNode;
}

/** Scroll position per history entry (location.key), so going back returns to where the guest was. */
const scrollByKey = new Map<string, number>();
const RESTORE_MS = 1200;

/**
 * Scrolls the page layer to `target`. A page still loading may be too short to reach it yet: keep trying
 * each frame for a moment, and stop the moment the guest touches or scrolls, or another navigation starts.
 */
function restoreScroll(el: HTMLDivElement, target: number, isCurrent: () => boolean) {
  el.scrollTop = target;
  if (target <= 0 || Math.abs(el.scrollTop - target) < 2) return;

  const deadline = performance.now() + RESTORE_MS;
  let stopped = false;
  const stop = () => {
    stopped = true;
    el.removeEventListener('touchstart', stop);
    el.removeEventListener('wheel', stop);
  };
  el.addEventListener('touchstart', stop, { passive: true });
  el.addEventListener('wheel', stop, { passive: true });

  const step = () => {
    if (stopped || !isCurrent()) return stop();
    el.scrollTop = target;
    if (Math.abs(el.scrollTop - target) < 2 || performance.now() > deadline) return stop();
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * iOS-style page transition — layer-based architecture.
 *
 * Each page is a full-screen absolutely-positioned layer with its OWN
 * scroll context.  No background flashes.
 *
 * Scroll: a new page (link, button) opens at the top; going back or forward in history (swipe, the app's
 * back arrow, the browser's arrows) returns to the position that history entry was left at — what
 * Safari's swipe preview shows, so the page does not jump after the swipe.
 *
 * Key insight for smooth skeleton visibility:
 *   The animation class is NOT applied during React's render/commit phase.
 *   Instead, after React commits the new children (skeleton) to the DOM,
 *   we position the container off-screen via an inline transform, then
 *   on the NEXT animation frame we swap in the CSS animation class.
 *   This gives the browser one frame to rasterize the skeleton content
 *   so it's visible from the very first frame of the slide.
 */
export default function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const containerRef   = useRef<HTMLDivElement>(null);
  const wrapperRef     = useRef<HTMLDivElement>(null);
  const prevPathRef    = useRef<string>(location.pathname);
  const snapshotRef    = useRef<HTMLDivElement | null>(null);
  const scrollPosRef   = useRef(0);
  const pendingAnimRef = useRef('');
  const rafRef         = useRef(0);
  const prevKeyRef     = useRef<string>(location.key);
  const scrollTargetRef = useRef<number | null>(null);
  const navSeqRef      = useRef(0);
  const [animClass, setAnimClass] = useState('');

  // Seed the navigation store on first render
  if (prevPathRef.current === location.pathname && !pendingAnimRef.current && !animClass) {
    useNavigationStore.getState().setCurrentPath(location.pathname);
  }

  // ── Render phase: detect route change ──
  if (location.pathname !== prevPathRef.current) {
    // Clean up leftover snapshot from rapid double-navigation
    if (snapshotRef.current?.parentElement) {
      snapshotRef.current.remove();
      snapshotRef.current = null;
    }

    // An edge swipe the browser already slid: no snapshot, no second slide — the page just appears.
    const browserAnimated = consumeBrowserAnimated();

    // Remember where the outgoing entry was left; pick where the incoming one should be.
    if (containerRef.current) {
      scrollPosRef.current = containerRef.current.scrollTop;
      scrollByKey.set(prevKeyRef.current, scrollPosRef.current);
    }
    prevKeyRef.current = location.key;
    scrollTargetRef.current = navigationType === 'POP' ? scrollByKey.get(location.key) ?? 0 : 0;
    navSeqRef.current += 1;

    if (containerRef.current && !browserAnimated) {
      const clone = containerRef.current.cloneNode(true) as HTMLDivElement;
      clone.className = 'page-transition-snapshot';
      snapshotRef.current = clone;
    }

    useNavigationStore.getState().setCurrentPath(location.pathname);
    const dir = useNavigationStore.getState().direction;
    prevPathRef.current = location.pathname;

    // Store the pending animation — don't set it as className yet
    pendingAnimRef.current =
      browserAnimated   ? '' :
      dir === 'forward' ? 'page-slide-in-right' :
      dir === 'back'    ? 'page-slide-in-left'  :
                          'page-fade-in';
  }

  // ── Before paint: insert snapshot, position container off-screen,
  //    then schedule the animation class for the next frame. ──
  useLayoutEffect(() => {
    const snap = snapshotRef.current;
    if (snap && wrapperRef.current && containerRef.current && !snap.parentElement) {
      wrapperRef.current.insertBefore(snap, containerRef.current);
      snap.scrollTop = scrollPosRef.current;
    }

    const pending = pendingAnimRef.current;
    const el = containerRef.current;
    if (scrollTargetRef.current !== null && el) {
      const seq = navSeqRef.current;
      restoreScroll(el, scrollTargetRef.current, () => navSeqRef.current === seq);
      scrollTargetRef.current = null;
    }
    if (pending && el) {
      pendingAnimRef.current = '';

      // Position off-screen immediately (inline style, no animation yet).
      // The browser will rasterize the children (spinner) in this layer.
      if (pending === 'page-slide-in-right') {
        el.style.transform = 'translate3d(100%, 0, 0)';
        el.style.opacity = '0';
      } else if (pending === 'page-slide-in-left') {
        el.style.transform = 'translate3d(-100%, 0, 0)';
        el.style.opacity = '0';
      } else {
        el.style.opacity = '0';
      }

      // Cancel any pending rAF from rapid double-navigation
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      // After one frame (content is now painted in the GPU layer),
      // remove the inline style and apply the CSS animation class.
      // We apply the class directly via DOM first so the animation's
      // fill-mode kicks in the SAME frame as the inline-style clear.
      // setAnimClass follows for React bookkeeping / cleanup.
      rafRef.current = requestAnimationFrame(() => {
        el.style.transform = '';
        el.style.opacity = '';
        el.classList.add(pending);
        setAnimClass(pending);
      });
    }
  });

  // ── Clean up when animation finishes ──
  const handleAnimationEnd = useCallback(() => {
    if (snapshotRef.current) {
      snapshotRef.current.remove();
      snapshotRef.current = null;
    }
    setAnimClass('');
  }, []);

  // Fallback cleanup if animationend never fires
  useEffect(() => {
    if (!animClass) return;
    const id = setTimeout(() => {
      if (snapshotRef.current) {
        snapshotRef.current.remove();
        snapshotRef.current = null;
      }
      setAnimClass('');
    }, 600);
    return () => clearTimeout(id);
  }, [animClass]);

  // Clean up rAF on unmount
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  return (
    <div ref={wrapperRef} className="page-transition-wrapper">
      <div
        ref={containerRef}
        className={`page-transition-container ${animClass}`}
        onAnimationEnd={handleAnimationEnd}
      >
        {children}
      </div>
    </div>
  );
}
